import React from 'react';
import {
    Card,
    Row,
    Col,
    Select,
    Space,
    Statistic,
    Empty
} from 'antd';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart
} from 'recharts';
import {
    LineChartOutlined,
    FallOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { displayNumbers } from '../../../../variables/numbersFunctions';

const EvolutionGraphiques = ({ data, exercice }) => {
    const [periodeType, setPeriodeType] = React.useState('mois'); // annees, mois, jours_mois, jours_semaine
    const [regroupement, setRegroupement] = React.useState('global'); // global, agence, organisme

    // Préparer les données selon la période sélectionnée
    const prepareDataByPeriode = () => {
        if (!data || data.length === 0) return [];

        switch (periodeType) {
            case 'annees':
                return prepareDataByAnnees();
            case 'mois':
                return prepareDataByMois();
            case 'jours_mois':
                return prepareDataByJoursMois();
            case 'jours_semaine':
                return prepareDataByJoursSemaine();
            default:
                return [];
        }
    };

    const prepareDataByAnnees = () => {
        const grouped = {};

        data.forEach(item => {
            const key = regroupement === 'global'
                ? 'Total'
                : regroupement === 'agence'
                    ? item.agenceNom
                    : item.organismeNom;

            const annee = item.exercice;

            if (!grouped[annee]) {
                grouped[annee] = { periode: annee.toString() };
            }

            if (!grouped[annee][key]) {
                grouped[annee][key] = {
                    emissions: 0,
                    annulations: 0,
                    net: 0
                };
            }

            grouped[annee][key].emissions += item.montantEmissions || 0;
            grouped[annee][key].annulations += item.montantAnnulations || 0;
            grouped[annee][key].net += item.montantNet || 0;
        });

        return Object.values(grouped).sort((a, b) =>
            parseInt(a.periode) - parseInt(b.periode)
        );
    };

    const prepareDataByMois = () => {
        const grouped = {};
        const moisNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
            'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

        data.forEach(item => {
            if (item.exercice !== exercice) return;

            const key = regroupement === 'global'
                ? 'Total'
                : regroupement === 'agence'
                    ? item.agenceNom
                    : item.organismeNom;

            const mois = item.mois || 1;
            const moisKey = `${mois}-${moisNames[mois - 1]}`;

            if (!grouped[moisKey]) {
                grouped[moisKey] = {
                    periode: moisNames[mois - 1],
                    moisNum: mois
                };
            }

            if (!grouped[moisKey][key]) {
                grouped[moisKey][key] = {
                    emissions: 0,
                    annulations: 0,
                    net: 0
                };
            }

            grouped[moisKey][key].emissions += item.montantEmissions || 0;
            grouped[moisKey][key].annulations += item.montantAnnulations || 0;
            grouped[moisKey][key].net += (item.montantEmissions - item.montantAnnulations) || 0;
        });

        return Object.values(grouped).sort((a, b) => a.moisNum - b.moisNum);
    };

    const prepareDataByJoursMois = () => {
        // Cette fonction nécessiterait des données plus détaillées par jour
        // Pour l'instant, retourner un tableau vide avec un message
        return [];
    };

    const prepareDataByJoursSemaine = () => {
        const joursNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
        // Cette fonction nécessiterait des données plus détaillées par jour de la semaine
        // Pour l'instant, simuler avec les données existantes
        return [];
    };

    const chartData = prepareDataByPeriode();
    console.log("Chart Data:", chartData);

    // Extraire les clés dynamiques (agences ou organismes)
    const getDynamicKeys = () => {
        if (chartData.length === 0) return [];
        const firstItem = chartData[0];
        return Object.keys(firstItem).filter(k =>
            k !== 'periode' && k !== 'moisNum'
        );
    };

    const dynamicKeys = getDynamicKeys();

    // Couleurs pour les différentes séries
    const colors = [
        '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
        '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
    ];

    // Calculer les statistiques d'évolution
    const calculateEvolution = () => {
        if (chartData.length < 2) return null;

        const first = chartData[0];
        const last = chartData[chartData.length - 1];

        let firstTotal = 0;
        let lastTotal = 0;

        dynamicKeys.forEach(key => {
            if (first[key]) firstTotal += first[key].net || 0;
            if (last[key]) lastTotal += last[key].net || 0;
        });

        const evolution = lastTotal - firstTotal;
        const evolutionPercent = firstTotal !== 0
            ? ((evolution / firstTotal) * 100).toFixed(2)
            : 0;

        return {
            evolution,
            evolutionPercent,
            isPositive: evolution >= 0
        };
    };

    const evolutionStats = calculateEvolution();

    // Formatter pour le tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: 'white',
                    padding: '12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, marginBottom: 8 }}>
                        {label}
                    </p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{
                            margin: '4px 0',
                            color: entry.color,
                            fontSize: '13px'
                        }}>
                            {entry.name}: {displayNumbers(entry.value)} F
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div>
            {/* Contrôles */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <label style={{ fontWeight: 500 }}>
                                <CalendarOutlined /> Période d'analyse
                            </label>
                            <Select
                                value={periodeType}
                                onChange={setPeriodeType}
                                style={{ width: '100%' }}
                                options={[
                                    { value: 'annees', label: 'Par années' },
                                    { value: 'mois', label: 'Par mois (année en cours)' },
                                    {
                                        value: 'jours_mois',
                                        label: 'Par jours du mois',
                                        disabled: true
                                    },
                                    {
                                        value: 'jours_semaine',
                                        label: 'Par jours de la semaine',
                                        disabled: true
                                    }
                                ]}
                            />
                        </Space>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <label style={{ fontWeight: 500 }}>Regroupement</label>
                            <Select
                                value={regroupement}
                                onChange={setRegroupement}
                                style={{ width: '100%' }}
                                options={[
                                    { value: 'global', label: 'Vue globale' },
                                    { value: 'agence', label: 'Par agence' },
                                    { value: 'organisme', label: 'Par organisme' }
                                ]}
                            />
                        </Space>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                        {evolutionStats && (
                            <Statistic
                                title="Évolution"
                                value={evolutionStats.evolutionPercent}
                                precision={2}
                                valueStyle={{
                                    color: evolutionStats.isPositive ? '#3f8600' : '#cf1322',
                                    fontSize: '20px'
                                }}
                                prefix={evolutionStats.isPositive ?
                                    <LineChartOutlined /> :
                                    <FallOutlined />
                                }
                                suffix="%"
                            />
                        )}
                    </Col>
                </Row>
            </Card>

            {chartData.length === 0 ? (
                <Card>
                    <Empty
                        description="Aucune donnée disponible pour cette période"
                    />
                </Card>
            ) : (
                <>
                    {/* Graphique en ligne - Production nette */}
                    <Card
                        title="Évolution de la production nette"
                        style={{ marginBottom: 16 }}
                    >
                        <ResponsiveContainer width="100%" height={350}>
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="periode" />
                                <YAxis
                                    tickFormatter={(value) => displayNumbers(value / 1000000) + 'M'}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                {dynamicKeys.map((key, index) => (
                                    <Line
                                        key={key}
                                        type="monotone"
                                        dataKey={`${key}.net`}
                                        name={key}
                                        stroke={colors[index % colors.length]}
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>

                    {/* Graphique combiné - Émissions vs Annulations */}
                    <Card
                        title="Émissions vs Annulations"
                        style={{ marginBottom: 16 }}
                    >
                        <ResponsiveContainer width="100%" height={350}>
                            <ComposedChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="periode" />
                                <YAxis
                                    tickFormatter={(value) => displayNumbers(value / 1000000) + 'M'}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                {dynamicKeys.map((key, index) => (
                                    <React.Fragment key={key}>
                                        <Bar
                                            dataKey={`${key}.emissions`}
                                            name={`${key} - Émissions`}
                                            fill={colors[index % colors.length]}
                                            opacity={0.8}
                                        />
                                        <Bar
                                            dataKey={`${key}.annulations`}
                                            name={`${key} - Annulations`}
                                            fill="#ff4d4f"
                                            opacity={0.6}
                                        />
                                    </React.Fragment>
                                ))}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Card>

                    {/* Graphique en aires empilées */}
                    <Card title="Production cumulée">
                        <ResponsiveContainer width="100%" height={350}>
                            <AreaChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="periode" />
                                <YAxis
                                    tickFormatter={(value) => displayNumbers(value / 1000000) + 'M'}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                {dynamicKeys.map((key, index) => (
                                    <Area
                                        key={key}
                                        type="monotone"
                                        dataKey={`${key}.net`}
                                        name={key}
                                        stackId="1"
                                        stroke={colors[index % colors.length]}
                                        fill={colors[index % colors.length]}
                                        fillOpacity={0.6}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </>
            )}
        </div>
    );
};

export default EvolutionGraphiques;